import React, { createContext, useContext, useReducer, useEffect } from 'react';

const SettingsContext = createContext();

const initialState = {
  apiKeys: {
    whisper: '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',
    googleTranslate: '',
    deepL: '6a9884cb-0b83-44d1-b0d3-0de8e9fefaf9:fx',
    pyannote: 'sk_1d62b058984d4e41a14aef9cd7d40f5f',
    huggingface: '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'
  },
  preferences: {
    defaultLanguage: 'en',
    defaultExportPath: '',
    theme: 'light',
    autoSave: true,
    maxWordsPerLine: 8,
    defaultResolution: '1080p',
    defaultFormat: '16:9',
    // Global subtitle font preferences
    subtitleFontFamily: 'Arial',
    subtitleArabicFontFamily: 'Bahij TheSansArabic',
    subtitleArabicFontPath: '/fonts/bahij-thesansarabic-bold.ttf'
  },
  loading: false,
  error: null
};

const settingsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    
    case 'SET_SETTINGS':
      return { ...state, ...action.payload, loading: false };
    
    case 'UPDATE_API_KEY':
      return {
        ...state,
        apiKeys: {
          ...state.apiKeys,
          [action.payload.service]: action.payload.key
        }
      };
    
    case 'UPDATE_PREFERENCE':
      return {
        ...state,
        preferences: {
          ...state.preferences,
          [action.payload.key]: action.payload.value
        }
      };
    
    case 'RESET_SETTINGS':
      return initialState;
    
    default:
      return state;
  }
};

export const SettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(settingsReducer, initialState);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      if (window.require) {
        const { ipcRenderer } = window.require('electron');
        const projectsDir = await ipcRenderer.invoke('get-projects-directory');
        const settingsPath = `${projectsDir}/settings.json`;
        
        try {
          const settingsData = await ipcRenderer.invoke('read-file', settingsPath);
          const settings = JSON.parse(settingsData);
          dispatch({ type: 'SET_SETTINGS', payload: settings });
        } catch (error) {
          // Settings file doesn't exist, create default settings
          console.log('Settings file not found, creating default settings');
          const defaultSettings = {
            apiKeys: initialState.apiKeys,
            preferences: initialState.preferences
          };
          
          try {
            await ipcRenderer.invoke('write-file', settingsPath, JSON.stringify(defaultSettings, null, 2));
            dispatch({ type: 'SET_SETTINGS', payload: defaultSettings });
          } catch (writeError) {
            console.error('Failed to create default settings file:', writeError);
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        }
      } else {
        // Fallback for development
        const savedSettings = localStorage.getItem('beevids-settings');
        if (savedSettings) {
          const settings = JSON.parse(savedSettings);
          dispatch({ type: 'SET_SETTINGS', payload: settings });
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
    }
  };

  const saveSettings = async () => {
    try {
      const settingsData = {
        apiKeys: state.apiKeys,
        preferences: state.preferences
      };

      if (window.require) {
        const { ipcRenderer } = window.require('electron');
        const projectsDir = await ipcRenderer.invoke('get-projects-directory');
        const settingsPath = `${projectsDir}/settings.json`;
        
        await ipcRenderer.invoke('write-file', settingsPath, JSON.stringify(settingsData, null, 2));
      } else {
        // Fallback for development
        localStorage.setItem('beevids-settings', JSON.stringify(settingsData));
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updateApiKey = async (service, key) => {
    try {
      dispatch({ type: 'UPDATE_API_KEY', payload: { service, key } });
      await saveSettings();
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const updatePreference = async (key, value) => {
    try {
      dispatch({ type: 'UPDATE_PREFERENCE', payload: { key, value } });
      const settingsData = {
        apiKeys: state.apiKeys,
        preferences: {
          ...state.preferences,
          [key]: value
        }
      };
      localStorage.setItem('beevids-settings', JSON.stringify(settingsData));
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const testApiKey = async (service) => {
    try {
      const key = state.apiKeys[service];
      if (!key) {
        throw new Error('API key not provided');
      }

      switch (service) {
        case 'whisper':
          // Test OpenAI Whisper API
          const TranscriptionService = (await import('../services/transcriptionService')).default;
          const transcriptionService = new TranscriptionService({ whisper: key });
          return await transcriptionService.testConnection();
          
        case 'googleTranslate':
          // Test Google Translate API
          console.log('Testing Google Translate API...');
          return { success: true, message: 'Google Translate API test (mock)' };
          
        case 'deepL':
          // Test DeepL API
          const TranslationService = (await import('../services/translationService')).default;
          const translationService = new TranslationService({ deepL: key });
          return await translationService.testApiKey();
          
        case 'pyannote':
          // Test Pyannote API
          console.log('Testing Pyannote API...');
          return { success: true, message: 'Pyannote API test (mock)' };
          
        case 'huggingface':
          // Test Hugging Face API
          try {
            const SimplePyannoteService = (await import('../services/simplePyannoteService')).default;
            const pyannoteService = new SimplePyannoteService(key);
            const result = await pyannoteService.checkConnection();
            return { 
              success: result.connected, 
              message: result.connected ? 'Hugging Face API connected successfully' : result.error 
            };
          } catch (error) {
            return { success: false, message: error.message };
          }
          
        default:
          throw new Error('Unknown service');
      }
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const resetSettings = async () => {
    try {
      dispatch({ type: 'RESET_SETTINGS' });
      
      if (window.require) {
        const { ipcRenderer } = window.require('electron');
        const projectsDir = await ipcRenderer.invoke('get-projects-directory');
        const settingsPath = `${projectsDir}/settings.json`;
        
        await ipcRenderer.invoke('write-file', settingsPath, JSON.stringify(initialState, null, 2));
      } else {
        localStorage.removeItem('beevids-settings');
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  };

  const value = {
    ...state,
    loadSettings,
    saveSettings,
    updateApiKey,
    updatePreference,
    testApiKey,
    resetSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};